/* global google */
import { useState, useEffect, Fragment } from 'react'
import is from 'is_js'
import _ from '@strings'
import classNames from 'classnames'
import { useDispatch, useSelector } from 'react-redux'
import { useGetStores } from '@hooks'
import { Sizer } from '@components/Ui/Grid'
import Spacer from '@components/Ui/Spacer'
import Text from '@components/Ui/Text'
import Button from '@components/Ui/Button'
import Icon from '@components/Ui/Icon'
import Loading from '@components/Ui/Loading'
import Error from '@components/Ui/Error'
import LocationAutoComplete from '@components/Modules/LocationAutoComplete'
import { setStores, userSelectStore, userFilteredStores } from '@store/actions/app'
import styles from './LocationSelect.scss'

const LocationSelect = ({ setValid }) => {
  const showStoreCount = 5
  const [currentStorePage, setCurrentStorePage] = useState(1)
  const storedStores = useSelector((s) => s.stores)
  const preFilteredStores = useSelector((s) => s.filteredStores)
  const selectedStore = useSelector((s) => s.data.store)
  const selectedTypeFilter = useSelector((s) => s.data.type.filter)
  const selectedChildFilter = useSelector((s) => s.data.child)
  const [selectedGeometry, setSelectedGeometry] = useState(null)
  const [stores, error, loading] = useGetStores()
  const [filteredStores, setFilteredStores] = useState(preFilteredStores || [])
  const [selected, setSelected] = useState(selectedStore)
  const dispatch = useDispatch()

  const hasStoredStores = is.not.empty(storedStores || [])
  const hasStores = is.not.empty(stores || [])
  const showStores = hasStoredStores ? storedStores : stores
  const selectedIsInFiltered = filteredStores.find((s) => s.postId === selectedStore?.postId)

  // const viennaPaedakustikCode = 'ATH03100'
  const viennaPaedakustikCode = 'ATH01121'

  const setSelectedBaseLocation = (lat, lng) => {
    setSelectedGeometry({ lat, lng })
  }

  const calcDistance = (a, b) =>
    (google.maps.geometry.spherical.computeDistanceBetween(a, b) / 1000).toFixed(2)

  const getNearestStores = ({ lat, lng }) => {
    const selectedLatLng = new google.maps.LatLng(lat, lng)

    const stores = [...(showStores || [])]
    setFilteredStores(
      stores
        .map((store) => {
          const { latitude, longitude } = store.coordinates
          const storeLatLng = new google.maps.LatLng(latitude, longitude)

          store.priority = false
          store.distance = calcDistance(selectedLatLng, storeLatLng) * 1000
          let hasFeatures = true

          selectedTypeFilter?.forEach((filter) => {
            if (store.type?.[filter] === false) {
              hasFeatures = false
            }
          })

          if (selectedChildFilter && !store.type?.paedakustik) {
            hasFeatures = false
          }

          if (
            selectedChildFilter &&
            store.code === viennaPaedakustikCode &&
            store.distance <= 30000
          ) {
            store.priority = 1
          }
          if (!selectedChildFilter && store.code === viennaPaedakustikCode) {
            return null
          }

          return hasFeatures ? { ...store } : null
        })
        .filter((x) => !!x)
        .sort((a, b) => {
          if (a.priority) {
            return -1
          }
          if (a.distance < b.distance) {
            return -1
          }
          if (a.distance > b.distance) {
            return 1
          }
          return 0
        })
    )

    setCurrentStorePage(1)
  }

  useEffect(() => {
    if (selected) {
      setValid(true)
    }
  }, [selected])

  useEffect(() => {
    if (!loading && hasStores) {
      dispatch(setStores(stores))
    }
  }, [stores, loading])

  useEffect(() => {
    if (selectedGeometry) {
      getNearestStores(selectedGeometry)
    }
  }, [selectedGeometry])

  useEffect(() => {
    if (filteredStores.length > 0) {
      dispatch(userFilteredStores(filteredStores))
    }
  }, [filteredStores])

  const onClick = (store) => {
    dispatch(userSelectStore(store))
    setSelected(store)
  }

  const renderDistance = (m) => {
    if (m < 1000) {
      return `${m} m`
    }

    return `${(m / 1000).toFixed(1)} km`
  }

  const renderStore = (store) => (
    <button
      key={store.code}
      store="button"
      onClick={() => onClick(store)}
      type="button"
      data-e2e="appointments-store"
      className={classNames(styles.item, {
        [styles.selected]: selected?.code === store.code,
      })}
    >
      <div className={styles.icon}>
        <Icon
          type={selected?.code === store.code ? 'pin' : 'pin'}
          size={selected?.code === store.code ? '24' : '24'}
        />
      </div>
      <div className={styles.storeText}>
        {selectedChildFilter && store?.code === viennaPaedakustikCode && (
          <Fragment>
            <Text as="div" type="robo_md_bold">
              {_('location.special_vienna')}
            </Text>
          </Fragment>
        )}
        {`${store?.contact?.address}, ${store?.contact?.zip} ${store?.contact?.city}`}
      </div>
      <div className={styles.distance}>{renderDistance(store.distance)}</div>
    </button>
  )

  const currentStores = filteredStores.slice(0, currentStorePage * showStoreCount)
  const maxPage = filteredStores?.length / showStoreCount

  return (
    <div className={styles.base}>
      {(!loading || hasStoredStores) && (
        <LocationAutoComplete
          disabled={loading || !hasStoredStores}
          onSelect={setSelectedBaseLocation}
        />
      )}
      <Spacer vertical scale="1" />
      {error && <Error>{_('errors.api')}</Error>}
      {loading && !hasStoredStores && <Loading>{_('location.loading')}</Loading>}
      {!selectedIsInFiltered && selectedStore && (
        <div>
          <Text type="robo_md_bold">{_('location.selected')}</Text>
          <Spacer vertical scale="0.5" />
          {renderStore(selectedStore)}
        </div>
      )}
      {is.not.empty(filteredStores) && (
        <Fragment>
          <Text type="robo_md_bold" center as="div">
            {_('location.select')}
          </Text>
          <Spacer vertical scale="1" />
          {currentStores?.map(renderStore)}
          {maxPage > currentStorePage && (
            <Fragment>
              <Spacer vertical scale="0.5" />
              <div style={{ textAlign: 'center' }}>
                <Button
                  onClick={() => setCurrentStorePage(currentStorePage + 1)}
                  theme="outline"
                  narrow
                >
                  {_('general.button_load_more')}
                </Button>
              </div>
            </Fragment>
          )}
        </Fragment>
      )}
    </div>
  )
}

LocationSelect.defaultProps = {
  setValid: () => {},
}
LocationSelect.propTypes = {}

export default LocationSelect
